import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, User } from 'lucide-react';

const LiveChatWidget = ({ isTriggered = false, onClose }) => {
  const [isOpen, setIsOpen] = useState(isTriggered);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [isStarted, setIsStarted] = useState(false);
  const messagesEndRef = useRef(null);

  // Use relative URL for production or env variable for development
  const backendUrl = window.location.hostname === 'localhost' 
    ? (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001')
    : '';  // Empty string = relative URL (same domain)

  // Handle external trigger
  useEffect(() => {
    if (isTriggered) {
      setIsOpen(true);
    }
  }, [isTriggered]);

  // Restore session from sessionStorage on mount
  useEffect(() => {
    const savedSessionId = sessionStorage.getItem('chatSessionId');
    const savedName = sessionStorage.getItem('chatCustomerName');
    const savedEmail = sessionStorage.getItem('chatCustomerEmail');
    
    if (savedSessionId && savedName) {
      setSessionId(savedSessionId);
      setCustomerName(savedName);
      setCustomerEmail(savedEmail || '');
      setIsStarted(true);
      setMessages([{
        role: 'system',
        content: `Welcome back, ${savedName}! Your previous session has been restored. How can I help you?`,
        timestamp: new Date().toISOString()
      }]);
    }
  }, []);

  // Auto scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Idle timeout checker - checks every 10 seconds
  useEffect(() => {
    if (!isStarted || !sessionId || !isOpen) return;

    const checkIdleInterval = setInterval(async () => {
      try {
        const response = await fetch(`${backendUrl}/api/chat/check-idle/${sessionId}`);
        const data = await response.json();

        if (data.success && data.idle_action) {
          if (data.idle_action === 'follow_up') {
            // Add follow-up message from AI
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: data.response,
              timestamp: new Date().toISOString()
            }]);
          } else if (data.idle_action === 'close') {
            // Add closing message and close chat
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: data.response,
              timestamp: new Date().toISOString()
            }]);
            
            // Close chat after 2 seconds
            setTimeout(() => {
              setIsOpen(false);
              setIsStarted(false);
              sessionStorage.removeItem('chatSessionId');
              sessionStorage.removeItem('chatCustomerName');
              sessionStorage.removeItem('chatCustomerEmail');
              if (onClose) onClose();
            }, 2000);
          }
        }
      } catch (error) {
        console.error('Error checking idle status:', error);
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(checkIdleInterval);
  }, [isStarted, sessionId, isOpen, backendUrl, onClose]);

  // Initial greeting message
  useEffect(() => {
    if (isOpen && messages.length === 0 && !isStarted) {
      setMessages([{
        role: 'assistant',
        content: "Hi! I'm here to help you with your gift card submissions. How can I assist you today?",
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isOpen]);

  const startChatSession = async () => {
    if (!customerName.trim()) {
      setMessages([{
        role: 'system',
        content: '⚠️ Please enter your name to start the chat.',
        timestamp: new Date().toISOString()
      }]);
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetch(`${backendUrl}/api/chat/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: customerName,
          customer_email: customerEmail
        })
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setSessionId(data.session_id);
        setIsStarted(true);
        
        // Save session to sessionStorage for persistence
        sessionStorage.setItem('chatSessionId', data.session_id);
        sessionStorage.setItem('chatCustomerName', customerName);
        sessionStorage.setItem('chatCustomerEmail', customerEmail || '');
        
        setMessages([{
          role: 'assistant',
          content: `Hi ${customerName}! I'm your support assistant. I can help you check your submission status, answer payment questions, or guide you through our process. What can I help you with today?`,
          timestamp: new Date().toISOString()
        }]);
      } else {
        throw new Error(data.message || 'Failed to start chat session');
      }
    } catch (error) {
      console.error('Error starting chat:', error);
      // More user-friendly error handling
      const errorMessage = error.message || 'Failed to start chat. Please try again.';
      // Show error in chat instead of alert
      setMessages([{
        role: 'system',
        content: `❌ Unable to connect to support. ${errorMessage}.\n\nPlease try:\n• Refreshing the page\n• Checking your internet connection\n• Contacting us at support@cashifygcmart.com`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage('');
    setIsLoading(true);

    try {
      // Show typing indicator immediately
      setIsTyping(true);
      
      // Fetch the AI response (using NEW Intelligence Layer v2)
      const response = await fetch(`${backendUrl}/api/chat/v2/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message: messageToSend,
          customer_name: customerName,
          customer_email: customerEmail
        })
      });

      const data = await response.json();

      if (data.success) {
        // Calculate "typing" delay based on response length
        // Simulate realistic typing speed
        const wordCount = data.response.split(' ').length;
        const typingTimePerWord = 80; // milliseconds per word for realistic typing
        const baseDelay = 1200; // Minimum 1.2 seconds delay
        const calculatedDelay = Math.min(baseDelay + (wordCount * typingTimePerWord), 5000); // Max 5 seconds
        
        // Add slight randomness for more natural feel
        const randomVariation = Math.random() * 800; // 0-800ms variation
        const finalDelay = calculatedDelay + randomVariation;

        // Keep showing typing indicator for calculated duration
        await new Promise(resolve => setTimeout(resolve, finalDelay));

        // Hide typing indicator
        setIsTyping(false);

        const aiMessage = {
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, aiMessage]);

        // Check if we should transfer to live chat
        if (data.should_transfer) {
          // Get transfer delay from backend (default 3 seconds)
          const transferDelay = (data.transfer_delay || 3) * 1000;
          
          // Trigger live chat widget after a delay (to feel natural, not instant)
          setTimeout(() => {
            try {
              // Check if live chat system is fully loaded and functional
              if (window.$zoho && window.$zoho.salesiq && typeof window.$zoho.salesiq.chatwindow !== 'undefined') {
                console.log('✅ Live chat system detected, attempting to open chat window...');
                
                // Try to open the chat window directly
                window.$zoho.salesiq.chatwindow.visible('show');
                
                // Also ensure the float button is visible if needed
                if (window.$zoho.salesiq.floatwindow) {
                  window.$zoho.salesiq.floatwindow.visible('show');
                }
                
                console.log('✅ Chat window opened successfully');
                
                // Check after a moment if chat window actually opened
                setTimeout(() => {
                  // If chat window still didn't open, backend message already has contact info
                  const chatVisible = document.querySelector('.zsiq_theme1, #siqiframe');
                  if (!chatVisible || window.getComputedStyle(chatVisible).display === 'none') {
                    console.warn('⚠️ Chat window did not appear (this is normal - backend message has contact info)');
                    // Don't add another message - backend already provided contact info
                  }
                }, 2000);
                
              } else {
                console.warn('⚠️ Live chat system not fully loaded (this is normal - backend message has fallback contact info)');
                // Don't show error - backend already provided contact information in its response
              }
            } catch (error) {
              console.error('❌ Error opening live chat:', error);
              // Don't show error message - backend response already includes contact info as fallback
            }
          }, transferDelay);
        }
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Even for errors, add a small delay to feel natural
      await new Promise(resolve => setTimeout(resolve, 1500));
      setIsTyping(false);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I apologize, I'm having trouble responding right now. Please try again or contact support@cashifygcmart.com",
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isStarted) {
        sendMessage();
      } else {
        startChatSession();
      }
    }
  };

  return (
    <>
      {/* Chat Button - Hide if triggered from menu (onClose prop exists) */}
      {!isOpen && !onClose && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 bg-gradient-to-r from-pink-500 to-purple-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 z-50"
          aria-label="Open Live Chat"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-white rounded-lg shadow-2xl flex flex-col z-50 border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-pink-500 to-purple-600 text-white p-4 rounded-t-lg flex justify-between items-center">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              <div>
                <h3 className="font-semibold">Live Support</h3>
                <p className="text-xs text-pink-100">We typically reply instantly</p>
              </div>
            </div>
            <button
              onClick={async () => {
                // Show confirmation if chat has started
                if (isStarted && messages.length > 1) {
                  const confirmed = window.confirm(
                    'Are you sure you want to end this chat session? Your conversation will be cleared.'
                  );
                  if (!confirmed) return;
                  
                  // Call backend to end session and log to support queries
                  try {
                    await fetch(`${backendUrl}/api/chat/end`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({
                        session_id: sessionId,
                        customer_email: customerEmail,
                        customer_name: customerName,
                        message: '' // Not used but required by schema
                      })
                    });
                  } catch (error) {
                    console.error('Error ending chat session:', error);
                  }
                  
                  // Reset all chat state
                  setMessages([]);
                  setIsStarted(false);
                  setSessionId(null);
                  setCustomerName('');
                  setCustomerEmail('');
                  setInputMessage('');
                  sessionStorage.removeItem('chatSessionId');
                  sessionStorage.removeItem('chatCustomerName');
                  sessionStorage.removeItem('chatCustomerEmail');
                }
                
                // Close the chat window
                setIsOpen(false);
                if (onClose) onClose();
              }}
              className="hover:bg-white/20 p-1 rounded transition-colors"
              aria-label="Close and end chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Chat Area */}
          {!isStarted ? (
            // Welcome Screen
            <div className="flex-1 p-6 flex flex-col justify-center">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Welcome to Support!</h3>
                <p className="text-sm text-gray-600">Let's get started. Please share your details:</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Your Name *</label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="John Doe"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Email (optional)</label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="john@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent outline-none"
                  />
                </div>

                <button
                  onClick={startChatSession}
                  className="w-full bg-gradient-to-r from-pink-500 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-pink-600 hover:to-purple-700 transition-all"
                >
                  Start Chat
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        msg.role === 'user'
                          ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                          : msg.role === 'system'
                          ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                          : 'bg-white text-gray-800 shadow-sm border border-gray-200'
                      }`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-6 h-6 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-xs font-semibold text-gray-600">Support Agent</span>
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-xs mt-1 ${msg.role === 'user' ? 'text-pink-100' : 'text-gray-400'}`}>
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
                
                {/* Typing Indicator */}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="max-w-[80%] rounded-lg p-3 bg-white text-gray-800 shadow-sm border border-gray-200">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-xs font-semibold text-gray-600">Support Agent</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="p-4 border-t border-gray-200 bg-white rounded-b-lg">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    disabled={isLoading}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent outline-none disabled:bg-gray-100"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={isLoading || !inputMessage.trim()}
                    className="bg-gradient-to-r from-pink-500 to-purple-600 text-white p-2 rounded-lg hover:from-pink-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Tips and End Chat Button */}
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-gray-500">
                    💡 Tip: Share your reference number for quick help
                  </p>
                  <button
                    onClick={async () => {
                      const confirmed = window.confirm(
                        'Are you sure you want to end this chat session? Your conversation will be cleared.'
                      );
                      if (confirmed) {
                        // Call backend to end session and log to support queries
                        try {
                          await fetch(`${backendUrl}/api/chat/end`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                              session_id: sessionId,
                              customer_email: customerEmail,
                              customer_name: customerName,
                              message: '' // Not used but required by schema
                            })
                          });
                        } catch (error) {
                          console.error('Error ending chat session:', error);
                        }
                        
                        // Reset all chat state
                        setMessages([]);
                        setIsStarted(false);
                        setSessionId(null);
                        setCustomerName('');
                        setCustomerEmail('');
                        setInputMessage('');
                        sessionStorage.removeItem('chatSessionId');
                        sessionStorage.removeItem('chatCustomerName');
                        sessionStorage.removeItem('chatCustomerEmail');
                        
                        // Show thank you message
                        alert('Chat session ended. Thank you for contacting us!');
                      }
                    }}
                    className="flex items-center gap-1 px-3 py-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors border border-red-200 hover:border-red-300"
                  >
                    <X className="w-3 h-3" />
                    End Chat
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default LiveChatWidget;
